import { styled, Typography, Box } from '@mui/material';
import ClassHoldingsInfo, {
  type HoldingClassDetails,
} from './ClassHoldingsInfo';
import { useState } from 'react';
import Spacing from 'components/Spacing';
import { Grid } from '@mui/material';
import HoldingClassRightsForm from 'routes/LpPages/Common/HoldingClass/HoldingClassRightsForm';
import Actions from 'components/Actions';
import Action from 'components/Actions/Action';
import { Delete } from '@mui/icons-material';
import Row from 'components/Layout/Row';
import ClassHoldingsHeader from './ClassHoldingsHeader';
import { useFormContext } from 'react-hook-form';
import api from 'lib/trpcClient';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import AppAccordion from 'components/AppAccordion';
import processAssetFullHistory from '../../helpers/processAssetFullHistory';
import AssetHistoryTable from '../../Revaluations/AssetHistoryTable';
import Heading from 'components/Heading';
import { FormLabel } from 'components/FormElements';
import { maxBy } from 'lodash';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AssetHistoryType } from 'further-types/lp/asset';

type ClassHoldingsListProps = {
  holdingClasses: Array<HoldingClassDetails>;
  onDeleteHoldingClass: (holdingClassId: string) => void;
  onUpdateHoldingClass: (holdingClassId: string) => void;
  isUpdatingHoldingClass: boolean;
  companyId: string;
  vehicleId: string;
  onRevalueClick: () => void;
  vehicleCurrencyCode: CurrencyCode;
};

const HoldingInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(18),
  alignSelf: 'center',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  '&.MuiBox-root': {
    gap: theme.spacing(0),
  },
}));

const StyledBorder = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.borderColor?.lightGrey}`,
  borderRadius: '16px',
  padding: theme.spacing(4),
}));

const StyledDiv = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.borderColor?.lightGrey}`,
  padding: theme.spacing(1.5),
}));

const ClassHoldingsList: React.FC<ClassHoldingsListProps> = ({
  holdingClasses,
  onDeleteHoldingClass,
  onUpdateHoldingClass,
  isUpdatingHoldingClass,
  companyId,
  vehicleId,
  vehicleCurrencyCode,
}) => {
  const [editingHoldingClassId, setEditingHoldingClassId] = useState<
    string | undefined
  >(undefined);

  const [holdingClassTotals] =
    api.asset.getAssetHoldingClassInvestmentTotals.useSuspenseQuery({
      assetId: companyId,
      vehicleId,
    });

  const [assetFullHistory] = api.asset.getAssetFullHistory.useSuspenseQuery({
    assetId: companyId,
    vehicleId,
  });

  const { control } = useFormContext();

  if (!holdingClasses) return null;

  return (
    <StyledBox>
      {holdingClasses.map((holdingClass, index) => {
        const isEditing = editingHoldingClassId === holdingClass._id;

        const totals = holdingClassTotals.find(
          (total) => total.holdingClassId === holdingClass._id,
        ) ?? {
          totalNumberOfUnits: 0,
          currentNumberOfUnits: 0,
          currentHoldValue: 0,
          currency: holdingClass.currency,
        };

        const holdingClassHistory = assetFullHistory[holdingClass._id] ?? [];

        const processedHistory = processAssetFullHistory({
          [holdingClass._id]: holdingClassHistory,
        });

        // Unit price should only come from revaluations or investments, not realisations
        const revaluationsAndInvestments = processedHistory.filter(
          (entry) =>
            entry.type === AssetHistoryType.Revaluation ||
            entry.type === AssetHistoryType.Investment,
        );
        const latestPriceEntry = maxBy(revaluationsAndInvestments, 'date');
        const latestUnitPrice = latestPriceEntry?.unitPrice ?? 0;

        return (
          <AppAccordion title={holdingClass.name} key={holdingClass._id}>
            <AppAccordion.Summary>
              <Row spacing="xs">
                <HoldingInfo>
                  {numberToDisplayString(totals.currentNumberOfUnits)} Units |{' '}
                  {numberToCurrencyString(totals.currentHoldValue, {
                    currency: totals.currency,
                  })}{' '}
                  value
                </HoldingInfo>
                <Actions stopPropagation color="primary">
                  <Action
                    label="Delete class"
                    icon={Delete}
                    color="error"
                    onClick={() => onDeleteHoldingClass(holdingClass._id)}
                  />
                </Actions>
              </Row>
            </AppAccordion.Summary>
            <AppAccordion.Content>
              <Spacing>
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Heading variant="h2">Valuation history</Heading>
                    <Spacing>
                      <Row>
                        <StyledDiv>
                          <Row centered justify="flex-start" spacing="xs">
                            <FormLabel
                              title="Current valuation:"
                              labelColor="text.secondary"
                            />
                            <Typography>
                              {numberToCurrencyString(totals.currentHoldValue, {
                                currency: totals.currency,
                              })}
                            </Typography>
                          </Row>
                        </StyledDiv>
                        <StyledDiv>
                          <Row centered justify="flex-start" spacing="xs">
                            <FormLabel
                              title="Current unit price"
                              labelColor="text.secondary"
                            />
                            <Typography>
                              {numberToCurrencyString(latestUnitPrice, {
                                currency: totals.currency,
                              })}
                            </Typography>
                          </Row>
                        </StyledDiv>
                      </Row>
                      {processedHistory.length > 0 && (
                        <AssetHistoryTable
                          assetHistory={processedHistory}
                          showHoldingClassName={false}
                          showSummary={false}
                        />
                      )}
                    </Spacing>
                  </Grid>
                </Grid>
                <StyledBorder>
                  <Grid container>
                    <Grid size={{ xs: 8 }}>
                      <Spacing>
                        <ClassHoldingsHeader
                          isEditing={isEditing}
                          editingHoldingClassId={editingHoldingClassId}
                          setIsEditing={(editing) =>
                            setEditingHoldingClassId(
                              editing ? holdingClass._id : undefined,
                            )
                          }
                          onUpdateHoldingClass={onUpdateHoldingClass}
                          isUpdatingHoldingClass={isUpdatingHoldingClass}
                        />
                        {isEditing ? (
                          <HoldingClassRightsForm
                            key={holdingClass._id}
                            index={index}
                            formName="holdingClasses"
                            control={control}
                            vehicleCurrencyCode={vehicleCurrencyCode}
                          />
                        ) : (
                          <ClassHoldingsInfo holdingClass={holdingClass} />
                        )}
                      </Spacing>
                    </Grid>
                  </Grid>
                </StyledBorder>
              </Spacing>
            </AppAccordion.Content>
          </AppAccordion>
        );
      })}
    </StyledBox>
  );
};

export default ClassHoldingsList;
